// MODULES
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { RouterModule } from "@angular/router";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { OverlayModule } from "@angular/cdk/overlay";
import {
  NgbDateParserFormatter,
  NgbDropdown,
  NgbModule,
} from "@ng-bootstrap/ng-bootstrap";
import { TranslateModule } from "@ngx-translate/core";
import { PerfectScrollbarModule } from "ngx-perfect-scrollbar";
import { ClickOutsideModule } from "ng-click-outside";
import { AutocompleteModule } from "./components/autocomplete/autocomplete.module";
import { PipeModule } from "app/shared/pipes/pipe.module";
import { NgxDatatableModule } from "@swimlane/ngx-datatable";
import { NgxSpinnerModule } from "ngx-spinner";
import { NgSelectModule } from "@ng-select/ng-select";
import { DragulaModule } from "ng2-dragula";
import { FileUploadModule } from "ng2-file-upload";
import { FullCalendarModule } from "@fullcalendar/angular";
import { ChartistModule } from "ng-chartist";
import { NgChartsModule } from "ng2-charts";
import { PdfViewerModule } from "ng2-pdf-viewer";

// DIRECTIVES
import { ToggleFullscreenDirective } from "./directives/toggle-fullscreen.directive";
import { SidebarLinkDirective } from "./directives/sidebar-link.directive";
import { SidebarDropdownDirective } from "./directives/sidebar-dropdown.directive";
import { SidebarAnchorToggleDirective } from "./directives/sidebar-anchor-toggle.directive";
import { SidebarDirective } from "./directives/sidebar.directive";
import { TopMenuDirective } from "./directives/topmenu.directive";
import { TopMenuLinkDirective } from "./directives/topmenu-link.directive";
import { TopMenuDropdownDirective } from "./directives/topmenu-dropdown.directive";
import { TopMenuAnchorToggleDirective } from "./directives/topmenu-anchor-toggle.directive";
import { CountUpDirective } from "./directives/count-up.directive";

// COMPONENTS
import { FooterComponent } from "./footer/footer.component";
import { NavbarComponent } from "./navbar/navbar.component";
import { HorizontalMenuComponent } from "./horizontal-menu/horizontal-menu.component";
import { VerticalMenuComponent } from "./vertical-menu/vertical-menu.component";
import { CustomizerComponent } from "./customizer/customizer.component";
import { NotificationSidebarComponent } from "./notification-sidebar/notification-sidebar.component";
import { InputFilterComponent } from "./components/input-filter/input-filter.component";
import { ViewDocumentComponent } from "./components/document/view-document/view-document.component";
import { DownloadDocumentComponent } from "./components/document/download-document/download-document.component";
import { PasswordInputComponent } from "./components/password-input/password-input.component";
import { DatePickerComponent } from "./components/date-picker/date-picker.component";
import { CalendarDatatableComponent } from "./components/calendar-datatable/calendar-datatable.component";
import { ShiftFormComponent } from "./components/shift-form/shift-form.component";
import { CvManagementComponent } from "./components/cv-management/cv-management.component";
import { UserCalendarStatsComponent } from "./components/user-calendar-stats/user-calendar-stats.component";
import { DashboardCalendarDonutStatsComponent } from "app/shared/components/dashboard/dashboard-calendar-donut-stats/dashboard-calendar-donut-stats.component";
import { DashboardCalendarChartjsBarStatsComponent } from "./components/dashboard/dashboard-calendar-chartjs-bar-stats/dashboard-calendar-chartjs-bar-stats.component";
import { RangeDatePickerComponent } from "./components/range-date-picker/range-date-picker.component";
import { DashboardAdminEmployeeComponent } from "./components/dashboard/dashboard-admin-employee/dashboard-admin-employee.component";
import { LoadScreenComponent } from "./components/load-screen/load-screen.component";
import { ModalTurnosComponent } from "./components/shift-form/modal-turnos.component";
import { LoginConfirmComponent } from "./components/login-confirm/login-confirm.component";
import { RegistrationTrackerNavbarComponent } from "./components/registration-tracker-navbar/registration-tracker-navbar.component";

// UTILS
import { NgbDateCustomParserFormatter } from "./utils/dateFormat";

@NgModule({
  exports: [
    CommonModule,
    FooterComponent,
    NavbarComponent,
    VerticalMenuComponent,
    HorizontalMenuComponent,
    CustomizerComponent,
    NotificationSidebarComponent,
    ToggleFullscreenDirective,
    SidebarDirective,
    TopMenuDirective,
    NgbModule,
    TranslateModule,
    InputFilterComponent,
    PasswordInputComponent,
    DatePickerComponent,
    CalendarDatatableComponent,
    NgxDatatableModule,
    NgxSpinnerModule,
    NgSelectModule,
    ViewDocumentComponent,
    DownloadDocumentComponent,
    DragulaModule,
    ShiftFormComponent,
    ModalTurnosComponent,
    CvManagementComponent,
    FileUploadModule,
    UserCalendarStatsComponent,
    CountUpDirective,
    FullCalendarModule,
    ChartistModule,
    DashboardCalendarDonutStatsComponent,
    DashboardCalendarChartjsBarStatsComponent,
    DashboardAdminEmployeeComponent,
    NgChartsModule,
    RangeDatePickerComponent,
    LoadScreenComponent,
    LoginConfirmComponent,
    RegistrationTrackerNavbarComponent
  ],
  imports: [
    RouterModule,
    CommonModule,
    NgbModule,
    TranslateModule,
    FormsModule,
    OverlayModule,
    ReactiveFormsModule,
    PerfectScrollbarModule,
    ClickOutsideModule,
    AutocompleteModule,
    PipeModule,
    NgxDatatableModule,
    NgxSpinnerModule,
    PdfViewerModule,
    NgSelectModule,
    DragulaModule.forRoot(),
    FileUploadModule,
    FullCalendarModule,
    ChartistModule,
    NgChartsModule
  ],
  declarations: [
    FooterComponent,
    NavbarComponent,
    VerticalMenuComponent,
    HorizontalMenuComponent,
    CustomizerComponent,
    NotificationSidebarComponent,
    ToggleFullscreenDirective,
    SidebarLinkDirective,
    SidebarDropdownDirective,
    SidebarAnchorToggleDirective,
    SidebarDirective,
    TopMenuLinkDirective,
    TopMenuDropdownDirective,
    TopMenuAnchorToggleDirective,
    TopMenuDirective,
    InputFilterComponent,
    PasswordInputComponent,
    DatePickerComponent,
    CalendarDatatableComponent,
    ViewDocumentComponent,
    DownloadDocumentComponent,
    ShiftFormComponent,
    CvManagementComponent,
    UserCalendarStatsComponent,
    CountUpDirective,
    RangeDatePickerComponent,
    DashboardCalendarDonutStatsComponent,
    DashboardCalendarChartjsBarStatsComponent,
    DashboardAdminEmployeeComponent,
    LoadScreenComponent,
    ModalTurnosComponent,
    LoginConfirmComponent,
    RegistrationTrackerNavbarComponent
  ],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
    NgbDropdown,
  ],
})
export class SharedModule { }
